import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HardHat, TrendingUp, Building2, ArrowRight, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { TopoBackground } from "@/components/TopoBackground";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/83fd665c-12e1-46ec-b34b-7c468a7eac31/images/b2f6e5ab41253c81562fd816070a774e44fec66d2c9f35c709d8c5d3b4a40c46.jpeg";

const iconCls = "h-9 w-9 text-[#163323]";
const IconTopoMap = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="8" width="32" height="32" rx="2" />
    <path d="M13 30c4-1 6-6 4-10M20 34c6-2 6-10 2-14M27 36c8-3 7-16 0-21M34 16c2 3 2 8 0 12" />
  </svg>
);
const IconCubes = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 8l7 4-7 4-7-4 7-4Z" />
    <path d="M15 24l7 4-7 4-7-4 7-4Z" />
    <path d="M33 24l7 4-7 4-7-4 7-4Z" />
  </svg>
);
const IconAsBuilt = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 6h16l8 8v20a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
    <path d="M28 6v8h8M16 20h10M16 26h6" />
    <circle cx="30" cy="30" r="3.5" />
    <path d="M30 33.5V38" />
  </svg>
);

const SERVICES = [
  { name: "Construction Surveying", short: "Construction\nSurveying", Icon: HardHat },
  { name: "Topographical Surveys", short: "Topographical\nSurveys", Icon: IconTopoMap },
  { name: "Grading & Slope Analysis", short: "Grading & Slope", Icon: TrendingUp },
  { name: "Volume Calculations", short: "Volume\nCalculations", Icon: IconCubes },
  { name: "Building Layouts", short: "Building Layouts", Icon: Building2 },
  { name: "Project As-Builts", short: "Project As-Builts", Icon: IconAsBuilt },
];

const Brand = () => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-12 w-12 object-contain" />
    <span className="leading-none">
      <span className="block font-serif text-xl font-bold tracking-[0.06em] text-[#163323] sm:text-2xl">
        DATA EARTH SURVEYING
      </span>
      <span className="mt-1 block text-[9px] font-medium uppercase tracking-[0.28em] text-[#5a6660]">
        Land · Construction · A Brighter Tomorrow
      </span>
    </span>
  </Link>
);

export default function Landing() {
  const navigate = useNavigate();
  const { img } = useSiteImages();
  const go = (name) => navigate(`/request?service=${encodeURIComponent(name)}`);
  const nav = [
    ["Home", "/"], ["About", "/about"], ["Services", "/services"],
    ["Projects", "/projects"], ["Contact", "/contact"],
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="relative z-50 bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Brand />
          <nav className="hidden items-center gap-8 lg:flex">
            {nav.map(([label, href]) => {
              const active = label === "Home";
              const cls = `relative py-1 text-sm font-medium tracking-wide transition-colors ${active ? "text-[#163323]" : "text-[#3d4742] hover:text-[#163323]"}`;
              return href.startsWith("/") ? (
                <Link key={label} to={href} className={cls}>
                  {label}
                  {active && <span className="absolute -bottom-0.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              ) : (
                <a key={label} href={href} className={cls}>{label}</a>
              );
            })}
          </nav>
          {/* right angled green panel */}
          <div className="hidden h-[64px] items-center xl:flex">
            <div
              className="flex h-full items-center bg-[#163323] pl-10 pr-8 text-[#c8a452]"
              style={{ clipPath: "polygon(22px 0, 100% 0, 100% 100%, 0 100%)" }}
            >
              <div className="text-[10px] font-semibold uppercase leading-[1.5] tracking-[0.25em]">
                Measure<br />Map<br />Build<br />Belong
              </div>
            </div>
          </div>
          <MobileNav buttonClassName="rounded-full bg-[#163323] p-2 text-white lg:hidden" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0d2116]">
        <img src={img("landing_hero")} alt="Data Earth surveyor with total station at a Bahamas construction site" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2116] via-[#0d2116]/85 to-transparent" />
        <TopoBackground className="opacity-100" opacity={0.08} />
        <div className="relative mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="mb-5 flex items-center gap-3">
                <span className="h-px w-10 bg-[#c8a452]" />
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                  The Bahamas &nbsp;/&nbsp; Our Ground &nbsp;/&nbsp; Brighter Possibilities
                </span>
              </div>
              <h1 className="font-serif text-5xl font-bold leading-[1.02] text-white sm:text-6xl lg:text-7xl">
                Precision From<br />the <span className="text-[#c8a452]">Ground Up</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/75">
                Professional land and construction surveying for confident planning, building, and development.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button
                  data-testid="hero-request-btn"
                  onClick={() => navigate("/request")}
                  className="group h-13 rounded-md bg-[#c1943a] px-8 py-3 text-base font-semibold text-white shadow-lg shadow-black/20 transition-all hover:-translate-y-0.5 hover:bg-[#a97f2c]"
                >
                  Request a Survey
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  data-testid="hero-explore-btn"
                  onClick={() => navigate("/services")}
                  variant="outline"
                  className="group h-13 rounded-md border-white/40 bg-transparent px-8 py-3 text-base font-semibold text-white hover:bg-white/10 hover:text-white"
                >
                  Explore Services
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </motion.div>
          </div>

          {/* right micro-copy */}
          <div className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 flex-col items-start gap-24 lg:flex">
            <div className="text-[11px] font-medium uppercase leading-[1.8] tracking-[0.28em] text-white/60">
              People<br />Land<br />Progress<br />Together
            </div>
            <div className="text-[11px] font-medium uppercase leading-[1.8] tracking-[0.28em] text-[#c8a452]/80">
              A More<br />Accurate<br />Tomorrow
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-[#c8a452]" />
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-[#b38f3f]">
              Expertise for a Stronger Tomorrow
            </span>
            <span className="h-px w-10 bg-[#c8a452]" />
          </div>
          <h2 className="mt-4 font-serif text-3xl font-bold text-[#163323] sm:text-4xl lg:text-[2.75rem]">
            Professional Surveying Services
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.name}
              data-testid={`home-service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => go(s.name)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="group flex flex-col items-center rounded-lg bg-[#f2f4f2] px-4 py-8 text-center transition-all hover:-translate-y-1 hover:bg-white hover:shadow-lg hover:shadow-[#163323]/5 hover:ring-1 hover:ring-[#c8a452]/40"
            >
              <s.Icon />
              <h3 className="mt-5 whitespace-pre-line font-serif text-base font-semibold leading-snug text-[#163323]">
                {s.short}
              </h3>
              <span className="mt-4 h-0.5 w-10 rounded bg-[#c8a452] transition-all group-hover:w-16" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* ACCURATE BAND */}
      <section className="relative overflow-hidden">
        <img src={img("landing_aerial")} alt="Aerial view of a Bahamian coastline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0d2116]/85" />
        <TopoBackground opacity={0.07} />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:py-20">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Accurate. Reliable. Built for Your Project.
          </h2>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-[#c8a452]" />
            <span className="text-xs font-medium uppercase tracking-[0.28em] text-white/70">
              Surveying Today for a Stronger Tomorrow
            </span>
            <span className="h-px w-10 bg-[#c8a452]" />
          </div>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              data-testid="band-request-btn"
              onClick={() => navigate("/request")}
              className="h-12 rounded-md bg-[#c1943a] px-7 font-semibold text-white hover:bg-[#a97f2c]"
            >
              Request a Survey <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="band-services-btn"
              onClick={() => navigate("/services")}
              variant="outline"
              className="h-12 rounded-md border-white/40 bg-transparent px-7 font-semibold text-white hover:bg-white/10 hover:text-white"
            >
              Explore Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
